import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    Link,
    List,
    ListItem,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    Typography,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { help } from '../../services/user.service'
import AccountCircle from '@mui/icons-material/AccountCircle'
import Support from '../../assets/img/help/support.png'
import { useTranslation } from 'react-i18next'
import { useFaqSuggestions } from '@/hooks/useFaqSuggestions'
import { sendEventToSkalin } from '@/utils/skalin'
import { useNavigate } from 'react-router-dom'

const HELP_DESK_URL = 'https://iodized-fisherman-e9c.notion.site/b926da99ecb34a9f8d116ef3ac0e1a97'
const FULL_HELP = {
    key: 'fullHelp',
    url: 'https://iodized-fisherman-e9c.notion.site/b926da99ecb34a9f8d116ef3ac0e1a97?v=d351f507b4384357b3b8104e0a99d587',
}

export const ContactForm = () => {
    const me = useSelector((state) => state.meStore?.me)
    const navigate = useNavigate()
    const { t } = useTranslation('contact')
    const [title, setTitle] = useState('')
    const [message, setMessage] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [selectedSite, setSelectedSite] = useState(null)
    const [openConfirm, setOpenConfirm] = useState(false)
    const suggestions = useFaqSuggestions(`${title} ${message}`)
    const eventSentRef = useRef(false)
    const minLength = 3

    useEffect(() => {
        if (me?.sites?.length > 0) setSelectedSite(me.sites[0])
    }, [me])

    useEffect(() => {
        if (suggestions.length > 0 && !eventSentRef.current) {
            sendEventToSkalin('help_suggestions_displayed')
            eventSentRef.current = true
        }
        if (suggestions.length === 0) {
            eventSentRef.current = false
        }
    }, [suggestions])

    const onSendClick = () => {
        setOpenConfirm(true)
    }

    const handleConfirmSubmit = async () => {
        setOpenConfirm(false)
        setError('')
        setLoading(true)

        if (!title || !message) {
            setError(t('errors.missingRequiredFields'))
        } else if (title.length < minLength || message.length < minLength) {
            setError(t('errors.minLength', { minLength }))
        } else {
            await help({
                title: `[${selectedSite.Name}] ${title}`,
                message,
                email: me.Email,
                hash: selectedSite.Hash,
            })
            setSuccess(true)
            setTitle('')
            setMessage('')
            navigate('/tickets')
        }

        setLoading(false)
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography component="h2" variant="h4" gutterBottom>
                        {t('pageTitle')}
                    </Typography>
                </Grid>

                <Grid item md={7} xs={12}>
                    <Grid container spacing={2}>
                        <Grid item md={8} xs={12}>
                            <TextField
                                label={t('fields.object')}
                                placeholder={t('fields.objectPlaceholder')}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                fullWidth
                                style={{ background: 'white' }}
                                required
                            />
                        </Grid>
                        <Grid item md={4} xs={12}>
                            <FormControl variant="standard" fullWidth>
                                <OutlinedInput
                                    value={me?.Email || ''}
                                    fullWidth
                                    disabled
                                    style={{ background: '#eeeeee' }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <AccountCircle />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item md={12}>
                            <FormControl variant="outlined" fullWidth>
                                <Select
                                    value={selectedSite?.Hash || ''}
                                    onChange={(e) => {
                                        const found = me.sites.find((s) => s.Hash === e.target.value)
                                        setSelectedSite(found)
                                    }}
                                    style={{ background: 'white' }}
                                    fullWidth
                                >
                                    {me?.sites?.map((s) => (
                                        <MenuItem key={s.Hash} value={s.Hash}>
                                            {s.Name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {(error || success) && (
                            <Grid item xs={12}>
                                {success && <Alert severity="success">{t('feedback.success')}</Alert>}
                                {error && (
                                    <Alert severity="error">
                                        {t('errors.missingRequiredFields') === error
                                            ? t('errors.missingRequiredFields')
                                            : t('errors.minLength', { minLength })}
                                    </Alert>
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                label={t('fields.message')}
                                placeholder={t('fields.messagePlaceholder')}
                                multiline
                                rows={6}
                                fullWidth
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                style={{ background: 'white' }}
                                required
                            />
                        </Grid>

                        {/* {suggestions.length > 0 && (
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <List>
                                    {suggestions.map((item) => (
                                        <ListItem key={item.key}>
                                            <Link href={item.url} target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>
                                                {t(`suggestions.${item.key}`)}
                                            </Link>
                                        </ListItem>
                                    ))}
                                    <ListItem>
                                        <Link href={FULL_HELP.url} target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>
                                            {t(`suggestions.${FULL_HELP.key}`)}
                                        </Link>
                                    </ListItem>
                                </List>
                            </Grid>
                        )} */}

                        <Grid item xs={12} sm={3}>
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <Button variant="contained" fullWidth onClick={onSendClick}>
                                    {t('actions.send')}
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item md={5} xs={12}>
                    <img src={Support} alt="support" style={{ width: '100%', height: 'auto' }} />
                </Grid>
            </Grid>

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>{t('confirmDialog.title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('confirmDialog.description')}</DialogContentText>
                    <List>
                        {suggestions.map((item) => (
                            <ListItem key={item.key}>
                                <Link href={item.url} target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>
                                    {t(`suggestions.${item.key}`)}
                                </Link>
                            </ListItem>
                        ))}
                    </List>

                    <Button variant="outlined" fullWidth href={FULL_HELP.url} target="_blank" rel="noopener">
                        {t(`suggestions.${FULL_HELP.key}`)}
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>{t('actions.cancel')}</Button>
                    <Button onClick={handleConfirmSubmit} variant="contained">
                        {t('actions.continue')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
